/*!
=========================================================
* Painel Toca da Onça — PDV (Ponto de Venda)
=========================================================
* Escaneia código de barras (qualquer leitor USB), monta carrinho, fecha venda.
* Caixa por vendedor/loja. Demo client-side; produção salva no CRM (/rest/vendas).
*/
import React from "react";
import { Card, CardHeader, CardBody, CardTitle, Row, Col, Input, Button, Table, Badge, FormGroup, Label } from "reactstrap";
import { supabase } from "supabaseClient";

const brl = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const LOJAS = ["Loja Centro", "Loja Shopping", "Ponto Norte"];
const VENDEDORES = ["Ana", "Bia", "Carla"];
const PAGAMENTOS = [
  { nome: "DINHEIRO", icon: "icon-coins" },
  { nome: "DÉBITO", icon: "icon-credit-card" },
  { nome: "CRÉDITO", icon: "icon-credit-card" },
  { nome: "PIX", icon: "icon-mobile" },
];

function Calculadora() {
  const [exp, setExp] = React.useState("");
  const tecla = (t) => {
    if (t === "C") return setExp("");
    if (t === "=") {
      try {
        if (!/^[\d+\-*/.() ]+$/.test(exp)) return;
        // eslint-disable-next-line no-new-func
        const r = Function('"use strict";return (' + exp + ")")();
        setExp(String(Math.round(r * 100) / 100));
      } catch (e) {}
      return;
    }
    setExp(exp + t);
  };
  const teclas = ["7", "8", "9", "/", "4", "5", "6", "*", "1", "2", "3", "-", "0", ".", "=", "+"];
  return (
    <div>
      <div style={{ background: "#0b1020", borderRadius: 8, padding: "10px 14px", textAlign: "right", fontSize: 22, color: "#00f2c3", minHeight: 42, marginBottom: 8, overflow: "hidden" }}>
        {exp || "0"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
        <button onClick={() => tecla("C")} style={{ gridColumn: "span 4", padding: 10, borderRadius: 8, border: "none", background: "rgba(253,93,147,.2)", color: "#fd5d93", cursor: "pointer" }}>C — limpar</button>
        {teclas.map((t) => (
          <button key={t} onClick={() => tecla(t)} style={{ padding: 12, borderRadius: 8, border: "1px solid rgba(255,255,255,.1)", background: "+-*/=".includes(t) ? "rgba(29,140,248,.2)" : "rgba(255,255,255,.05)", color: "#fff", fontSize: 16, cursor: "pointer" }}>{t}</button>
        ))}
      </div>
    </div>
  );
}

function PDV() {
  const [loja, setLoja] = React.useState(LOJAS[0]);
  const [vendedor, setVendedor] = React.useState(VENDEDORES[0]);
  const [codigo, setCodigo] = React.useState("");
  const [carrinho, setCarrinho] = React.useState([]);
  const [caixaDia, setCaixaDia] = React.useState(0);
  const [vendasDia, setVendasDia] = React.useState(0);
  const [pagamento, setPagamento] = React.useState("DINHEIRO");
  const [resultados, setResultados] = React.useState([]);
  const [buscando, setBuscando] = React.useState(false);
  const [naoAchou, setNaoAchou] = React.useState(null);
  const inputRef = React.useRef(null);

  const foco = () => inputRef.current && inputRef.current.focus();
  React.useEffect(foco, []);

  const hojeStr = () => new Date().toISOString().slice(0, 10);
  const lerVendas = () => {
    try { return JSON.parse(localStorage.getItem("vendas_pdv") || "[]"); } catch (e) { return []; }
  };
  React.useEffect(() => {
    const hoje = hojeStr();
    const doVendedor = lerVendas().filter((v) => v.data === hoje && v.vendedor === vendedor && v.loja === loja);
    setCaixaDia(doVendedor.reduce((s, v) => s + v.total, 0));
    setVendasDia(doVendedor.length);
  }, [vendedor, loja]);

  const addCarrinho = (p) => {
    setCarrinho((c) => {
      const id = p.sku || p.id;
      const ex = c.find((i) => i.codigo === id);
      if (ex) return c.map((i) => (i.codigo === id ? { ...i, qtd: i.qtd + 1 } : i));
      return [...c, { codigo: id, nome: p.title, preco: Number(p.price) || 0, qtd: 1 }];
    });
  };

  const escanear = async (e) => {
    e.preventDefault();
    const cod = codigo.trim();
    if (!cod) return;
    setBuscando(true);
    setNaoAchou(null);
    setResultados([]);
    setCodigo("");
    // 1) tenta SKU exato (leitor de etiqueta)
    let { data: exato } = await supabase.from("products").select("id,sku,title,price,wholesale_price,stock").eq("sku", cod).limit(1);
    if (exato && exato.length) {
      addCarrinho(exato[0]);
      setBuscando(false);
      foco();
      return;
    }
    // 2) busca por nome
    const { data: achados } = await supabase
      .from("products").select("id,sku,title,price,wholesale_price,stock")
      .ilike("title", "%" + cod + "%").eq("status", "active").limit(8);
    setBuscando(false);
    if (achados && achados.length === 1) addCarrinho(achados[0]);
    else if (achados && achados.length) setResultados(achados);
    else setNaoAchou(cod);
    foco();
  };

  const mudarQtd = (cod, d) =>
    setCarrinho((c) => c.map((i) => (i.codigo === cod ? { ...i, qtd: Math.max(1, i.qtd + d) } : i)));
  const remover = (cod) => setCarrinho((c) => c.filter((i) => i.codigo !== cod));

  const total = carrinho.reduce((s, i) => s + i.preco * i.qtd, 0);

  const finalizar = () => {
    if (!carrinho.length) return;
    const venda = { data: hojeStr(), ts: Date.now(), loja, vendedor, itens: carrinho, total, pagamento };
    const todas = lerVendas();
    todas.push(venda);
    localStorage.setItem("vendas_pdv", JSON.stringify(todas));
    // grava também no banco do site (via bridge local, chave secreta protegida)
    fetch("http://localhost:8787/venda", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ loja, vendedor, itens: carrinho, total, forma_pagamento: pagamento }),
    }).catch(() => {});
    setCaixaDia((v) => v + total);
    setVendasDia((n) => n + 1);
    setCarrinho([]);
    foco();
  };

  return (
    <div className="content">
      <Row>
        <Col md="4">
          <FormGroup>
            <Label className="text-muted">Loja / ponto de retirada</Label>
            <Input type="select" value={loja} onChange={(e) => setLoja(e.target.value)}>
              {LOJAS.map((l) => <option key={l}>{l}</option>)}
            </Input>
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label className="text-muted">Vendedor(a)</Label>
            <Input type="select" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
              {VENDEDORES.map((v) => <option key={v}>{v}</option>)}
            </Input>
          </FormGroup>
        </Col>
        <Col md="4">
          <Card className="card-stats" style={{ marginBottom: 0 }}>
            <CardBody style={{ padding: "12px 16px" }}>
              <p className="card-category text-muted" style={{ margin: 0 }}>Caixa de {vendedor} hoje</p>
              <CardTitle tag="h3" className="text-success" style={{ margin: 0 }}>{brl(caixaDia)} <small className="text-muted" style={{ fontSize: 13 }}>· {vendasDia} vendas</small></CardTitle>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="7" md="12">
          <Card>
            <CardHeader><CardTitle tag="h4"><i className="tim-icons icon-barcode text-info" /> Escaneie o produto</CardTitle></CardHeader>
            <CardBody>
              <form onSubmit={escanear}>
                <Input
                  innerRef={inputRef}
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="Passe o leitor ou digite o código e Enter"
                  style={{ fontSize: 20, height: 52 }}
                  autoFocus
                />
              </form>

              {buscando && <p className="text-info" style={{ marginTop: 12 }}><i className="tim-icons icon-refresh-02" /> buscando no banco do site...</p>}

              {resultados.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <p className="text-muted" style={{ marginBottom: 6 }}>Achei {resultados.length} — toque no certo:</p>
                  {resultados.map((p) => (
                    <div key={p.id} onClick={() => { addCarrinho(p); setResultados([]); foco(); }}
                      style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: "rgba(29,205,159,.06)", marginBottom: 6 }}>
                      <span className="text-white">{p.title}</span>
                      <span className="text-success">{brl(p.price)}</span>
                    </div>
                  ))}
                </div>
              )}

              {naoAchou && (
                <div style={{ marginTop: 14, background: "rgba(255,176,32,.1)", border: "1px solid rgba(255,176,32,.4)", borderRadius: 10, padding: 14 }}>
                  <p className="text-warning" style={{ margin: 0 }}>
                    <i className="tim-icons icon-alert-circle-exc" /> Nada encontrado pra <b>{naoAchou}</b> no banco do site. Confira o nome/código ou cadastre o produto no site.
                  </p>
                </div>
              )}

              <Table className="tablesorter" responsive style={{ marginTop: 16 }}>
                <thead className="text-primary">
                  <tr><th>Produto</th><th className="text-center">Preço</th><th className="text-center">Qtd</th><th className="text-center">Subtotal</th><th></th></tr>
                </thead>
                <tbody>
                  {carrinho.map((i) => (
                    <tr key={i.codigo}>
                      <td>{i.nome}</td>
                      <td className="text-center">{brl(i.preco)}</td>
                      <td className="text-center">
                        <Button size="sm" color="link" className="text-white" onClick={() => mudarQtd(i.codigo, -1)}>−</Button>
                        {i.qtd}
                        <Button size="sm" color="link" className="text-white" onClick={() => mudarQtd(i.codigo, 1)}>+</Button>
                      </td>
                      <td className="text-center">{brl(i.preco * i.qtd)}</td>
                      <td className="text-center"><Button size="sm" color="link" className="text-danger" onClick={() => remover(i.codigo)}><i className="tim-icons icon-simple-remove" /></Button></td>
                    </tr>
                  ))}
                  {carrinho.length === 0 && <tr><td colSpan="5" className="text-muted">Carrinho vazio — escaneie um produto.</td></tr>}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        <Col lg="5" md="12">
          <Card>
            <CardHeader><CardTitle tag="h4">Fechar venda</CardTitle></CardHeader>
            <CardBody>
              <div style={{ background: "rgba(29,205,159,.08)", borderRadius: 10, padding: 16, marginBottom: 14 }}>
                <p className="text-muted" style={{ margin: 0 }}>Total da venda</p>
                <h1 className="text-success" style={{ margin: "4px 0" }}>{brl(total)}</h1>
                <p className="text-muted" style={{ margin: 0 }}>{carrinho.reduce((s, i) => s + i.qtd, 0)} itens</p>
              </div>
              <Label className="text-muted">Forma de pagamento — aperte a tecla</Label>
              <Row style={{ marginBottom: 14 }}>
                {PAGAMENTOS.map((p, i) => (
                  <Col xs="6" key={p.nome} style={{ marginBottom: 8 }}>
                    <Button block color={pagamento === p.nome ? "success" : "default"} outline={pagamento !== p.nome}
                      onClick={() => setPagamento(p.nome)} style={{ height: 56, fontSize: 15 }}>
                      <span style={{ opacity: 0.6, marginRight: 6 }}>{i + 1}</span>
                      <i className={"tim-icons " + p.icon} style={{ marginRight: 6 }} /> {p.nome}
                    </Button>
                  </Col>
                ))}
              </Row>
              <Button color="success" block size="lg" onClick={finalizar} disabled={!carrinho.length}>
                <i className="tim-icons icon-check-2" /> Finalizar venda (Enter)
              </Button>
              <p className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
                Grava no caixa e no banco do site. A taxa da maquininha sai só no seu relatório.
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardHeader><CardTitle tag="h4"><i className="tim-icons icon-atom text-info" /> Calculadora</CardTitle></CardHeader>
            <CardBody><Calculadora /></CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default PDV;
